import CTASection from "@/components/CTASection";
import {
  CATEGORY_BY_SLUG,
  RESIDENTIAL_CATEGORIES,
  RESIDENTIAL_GALLERY,
} from "@/lib/siteData";

const ResidentialPage = () => {
  const category = CATEGORY_BY_SLUG["residential"];

  return (
    <div data-testid="residential-page">
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[80vh] min-h-[560px] overflow-hidden">
          <img
            src={category.hero}
            alt="Residential storage — TV units, sideboards, display units"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto w-full px-5 md:px-10 pb-14 md:pb-24">
              <div className="max-w-3xl text-white">
                <div className="inline-block bg-black/50 backdrop-blur-sm px-3 py-1.5 mb-5">
                  <div className="overline" style={{ color: "#F8D16F" }}>
                    Residential storage
                  </div>
                </div>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight font-light">
                  Room by Room,<br />
                  <span className="italic">Considered.</span>
                </h1>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 reveal">
            <div className="overline mb-4">Overview</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Storage for<br />
              <span className="italic text-teal">every room.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal space-y-5 text-lg md:text-xl text-black/75 leading-relaxed">
            <p>
              Everything outside the kitchen, wardrobe, and office —
              TV units, shoe racks, consoles and sideboards, bookshelves and
              display units, beds and bedside tables, study tables, dressing
              tables — the pieces that hold everyday life together.
            </p>
            <p>
              Every module is optimised for modern Indian homes: real ceiling
              heights, wall thicknesses, and space constraints — designed to
              maximise storage without crowding the room.
            </p>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section
        data-testid="residential-categories"
        className="bg-cream py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-16 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Category grid</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                Six families,<br />
                <span className="italic text-navy">many pieces.</span>
              </h2>
            </div>
            <p className="md:text-right md:max-w-sm text-black/60">
              The main storage families for the home — each one designed to slot
              cleanly into the room it lives in.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {RESIDENTIAL_CATEGORIES.map((c, i) => (
              <div
                key={c.name}
                data-testid={`residential-category-${c.name.toLowerCase().replace(/[\s&/]+/g, "-")}`}
                className="reveal group"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[4/5] overflow-hidden bg-ivory">
                  <img
                    src={c.image}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                  />
                </div>
                <div className="mt-5">
                  <div className="font-display text-2xl leading-tight text-navy">
                    {c.name}
                  </div>
                  <p className="mt-2 text-sm md:text-base text-black/70 leading-relaxed">
                    {c.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section
        data-testid="residential-gallery-strip"
        className="bg-ivory py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="mb-10 md:mb-14 reveal">
            <div className="max-w-2xl">
              <div className="overline mb-4">Recent installs</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
                Rooms &amp;{" "}
                <span className="italic text-navy">real homes.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {RESIDENTIAL_GALLERY.map((src, i) => (
              <div
                key={i}
                className="reveal group bg-white p-2 md:p-3 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)] ring-1 ring-navy/10 transition-transform duration-500 hover:-translate-y-1"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-cream">
                  <img
                    src={src}
                    alt={`Residential project ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 pointer-events-none bg-gradient-to-t from-black/45 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute left-3 bottom-3 text-white text-[10px] md:text-xs tracking-[0.24em] uppercase opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-500">
                    Project 0{i + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Ready to design yours?"
        title="Get a Quotation."
        body="Share your room plan or requirement and our team will respond with a scope, timeline, and indicative pricing."
        primaryLabel="Get a Quotation"
        primaryTo="/contact"
        secondaryLabel="Become a Dealer"
        secondaryTo="/become-a-dealer"
        testid="residential-cta"
      />
    </div>
  );
};

export default ResidentialPage;
